/** Milestone 3 admin shapes (docs/API.md "Milestone 3 additions", Admin). */
import { z } from 'zod'
import { Address, Cursor, Hash32, IsoTimestamp, MicroString, PageLimit, UsdString, Uuid, WeiString } from '../primitives.js'
import { AgentStatus, FeeCategory, FeeSource, JobKind, JobStatus, PlanId } from '../enums.js'
import { AgentSummary, JsonObject } from './common.js'
import { AgentCard } from './agents.js'
import { User } from './identity.js'

/** GET /admin/overview. */
export const AdminOverview = z.object({
  asOf: IsoTimestamp,
  counts: z.object({
    users: z.number().int().nonnegative(),
    agents: z.number().int().nonnegative(),
    activeAgents: z.number().int().nonnegative(),
    /** Trading wallets arbitrage derived for customers. */
    wallets: z.number().int().nonnegative(),
    activePermissions: z.number().int().nonnegative(),
    tokens: z.number().int().nonnegative(),
  }),
  queue: z.object({
    queued: z.number().int().nonnegative(),
    running: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    dead: z.number().int().nonnegative(),
    oldestQueuedAt: IsoTimestamp.nullable(),
  }),
  /** Per indexer cursor (payments, token holders, ...): blocks behind head. */
  indexers: z.array(z.object({ name: z.string(), lagBlocks: z.number().int().nonnegative().nullable(), updatedAt: IsoTimestamp.nullable() })),
  modelSpendTodayMicro: MicroString,
  // L6-admin additions (additive): breakdowns for the overview tiles and charts.
  /** Agents per status (draft, active, paused, retired). */
  agentsByStatus: z.object({
    draft: z.number().int().nonnegative(),
    active: z.number().int().nonnegative(),
    paused: z.number().int().nonnegative(),
    retired: z.number().int().nonnegative(),
  }),
  /** Queue per job kind: queued/running depth plus failed and dead counts, busiest first. */
  queueByKind: z.array(
    z.object({
      kind: z.string(),
      queued: z.number().int().nonnegative(),
      running: z.number().int().nonnegative(),
      failed: z.number().int().nonnegative(),
      dead: z.number().int().nonnegative(),
    }),
  ),
  /** Charged model spend per UTC day, last 7 days oldest first (today last, equal to `modelSpendTodayMicro`). */
  modelSpendByDay: z.array(z.object({ day: z.string(), chargedMicro: MicroString, providerCostMicro: MicroString })),
  /**
   * Retired: the Rust stream's cursor table (`stream.cursors`) was dropped in migration 0045 (the market stream lives
   * in arbitrage), so this is always null. Kept so older clients still parse the overview.
   */
  stream: z.object({ lagBlocks: z.number().int().nonnegative().nullable(), updatedAt: IsoTimestamp.nullable() }).nullable(),
  /** Chain head used for the lag figures; null when the RPC did not answer. */
  headBlock: z.number().int().nonnegative().nullable(),
})
export type AdminOverview = z.output<typeof AdminOverview>

export const AdminAgentsQuery = z.object({ status: AgentStatus.optional(), cursor: Cursor.optional(), limit: PageLimit })

export const AdminAgentRow = z.object({
  agent: AgentCard,
  creator: z.object({ id: Uuid, handle: z.string().nullable(), primaryAddress: Address }),
  permissionsActive: z.number().int().nonnegative(),
  subscribersCount: z.number().int().nonnegative(),
  createdAt: IsoTimestamp,
})
export type AdminAgentRow = z.output<typeof AdminAgentRow>

export const AdminUsersQuery = z.object({ cursor: Cursor.optional(), limit: PageLimit })

export const AdminUserRow = z.object({
  user: User,
  planId: PlanId.nullable(),
  agentsCount: z.number().int().nonnegative(),
  walletsCount: z.number().int().nonnegative(),
  lastSeenAt: IsoTimestamp.nullable(),
})
export type AdminUserRow = z.output<typeof AdminUserRow>

export const AdminJobsQuery = z.object({ status: JobStatus.optional(), kind: JobKind.optional(), cursor: Cursor.optional(), limit: PageLimit })

/** `jobs.jobs` row. `kind` is text in the table, so unknown kinds pass through. */
export const AdminJob = z.object({
  id: Uuid,
  kind: z.string(),
  tenantKey: z.string(),
  status: JobStatus,
  attempts: z.number().int().nonnegative(),
  maxAttempts: z.number().int().nonnegative(),
  runAt: IsoTimestamp,
  lastError: z.string().nullable(),
  payload: JsonObject,
  createdAt: IsoTimestamp,
  updatedAt: IsoTimestamp,
})
export type AdminJob = z.output<typeof AdminJob>

/** `billing.fee_ledger` rows without `reconciled_at`. */
export const AdminUnreconciledFee = z.object({
  id: Uuid,
  agent: AgentSummary,
  category: FeeCategory,
  source: FeeSource,
  sourceRef: z.string(),
  amountUsd: UsdString,
  token: Address.nullable(),
  amountWei: WeiString.nullable(),
  collectedAt: IsoTimestamp,
  /**
   * L6-admin (additive): the treasury deposit instruction for this row, ready to copy: one
   * `FeeRouter.collect(agentId, token, amount, category, ref)` with `ref = keccak256(sourceRef)` (the reconciler
   * marks the row reconciled once that `FeeCollected` is indexed). Rows come ordered by agent, category, time.
   */
  instruction: z.object({
    router: Address,
    agentId: Hash32,
    token: Address,
    /** Token units: `amountWei` when recorded, else the USD amount in USDG units (6 decimals). */
    amount: WeiString,
    category: z.enum(['SUBSCRIPTION', 'EXECUTION', 'PERFORMANCE']),
    ref: Hash32,
    holderEligible: z.boolean(),
    call: z.string(),
  }),
})
export type AdminUnreconciledFee = z.output<typeof AdminUnreconciledFee>
